import { Check, Clock, ExternalLink, MapPin, Phone } from "lucide-react";
import { CAMPING_YEAR, ENTRY_FEES, ENTRY_FEE_DISCLAIMER, formatCOP, formatUSD } from "@/constants";

const seasons = [
  {
    label: "Temporada seca",
    range: "Dic – Abr",
    note: "Ideal para camping. Menos lluvias y mar más calmo.",
  },
  {
    label: "Temporada de lluvias",
    range: "May – Nov",
    note: "Paisajes más verdes. Mayor probabilidad de precipitaciones.",
  },
  {
    label: "Temperatura",
    range: "28°C",
    note: "Promedio anual. Alta humedad relativa todo el año.",
  },
];

const tips = [
  "Lleva suficiente agua potable y comida",
  "Usa protector solar y repelente de insectos",
  "Calzado antideslizante para los senderos",
  "Respeta la fauna y flora del parque",
  "No dejes basura en los senderos o playas",
];

const links = [
  {
    label: "Parques Nacionales",
    href: "https://www.parquesnacionales.gov.co/nuestros-parques/pnn-tayrona/",
  },
  { label: "Colombia Travel", href: "https://www.colombia.travel" },
  { label: "ProColombia", href: "https://www.procolombia.co" },
  { label: "Pronóstico IDEAM", href: "https://www.ideam.gov.co" },
];

/**
 * How to visit (DESIGN.md): access chips, the fee table in a Tayrona-green
 * panel, seasons, tips and official links. Fees come from ENTRY_FEES
 * (Resolución 551 de 2025); the disclaimer always sits next to them.
 */
const TourismInfo = () => (
  <section id="tourism-info" aria-labelledby="tourism-heading" className="pq-section">
    <div className="pq-wrap" style={{ display: "grid", gap: 48 }}>
      <div style={{ display: "grid", gap: 20 }}>
        <h2 id="tourism-heading" className="pq-headline">
          Cómo visitar el Parque Nacional Natural Tayrona.
        </h2>
        <p className="pq-lead">
          Precios de entrada, temporadas y recomendaciones para planear tu visita
          en {CAMPING_YEAR}.
        </p>
        <ul className="pq-tags" aria-label="Datos de acceso">
          <li className="pq-chip"><MapPin size={16} aria-hidden="true" /> Km 34, vía Santa Marta a Riohacha</li>
          <li className="pq-chip"><Clock size={16} aria-hidden="true" /> Lunes a domingo, 8:00 a 17:00</li>
          <li className="pq-chip">
            <Phone size={16} aria-hidden="true" />
            <a href="tel:+5754230404" className="pq-link">+57 (5) 423-0404</a>
          </li>
        </ul>
      </div>

      <div className="pq-panel pq-tema-tayrona pq-panel-stack">
        <div className="pq-panel-body">
          <h3 className="pq-headline">Tarifas de entrada {CAMPING_YEAR}.</h3>
          <p className="pq-panel-note">
            Resolución No. 551 de 2025, Parques Nacionales Naturales de Colombia.
            Por persona, en pesos colombianos. El valor en dólares es aproximado.
          </p>
        </div>
        <div className="pq-fees" role="table" aria-label={`Tarifas de entrada ${CAMPING_YEAR}`}>
          <div className="pq-fees-row pq-fees-head" role="row">
            <span role="columnheader">Visitante</span>
            <span role="columnheader">Temporada baja</span>
            <span role="columnheader">Temporada alta</span>
            <span role="columnheader">Bahía Concha (todo el año)</span>
          </div>
          {ENTRY_FEES.map(({ group, lowSeasonCOP, highSeasonCOP, bahiaConchaCOP }) => (
            <div key={group} className="pq-fees-row" role="row">
              <span role="cell">{group}</span>
              <span role="cell">
                <strong>{formatCOP(lowSeasonCOP)}</strong>
                <small>{formatUSD(lowSeasonCOP)}</small>
              </span>
              <span role="cell">
                <strong>{formatCOP(highSeasonCOP)}</strong>
                <small>{formatUSD(highSeasonCOP)}</small>
              </span>
              <span role="cell">
                <strong>{formatCOP(bahiaConchaCOP)}</strong>
                <small>{formatUSD(bahiaConchaCOP)}</small>
              </span>
            </div>
          ))}
        </div>
        <p className="pq-panel-note">{ENTRY_FEE_DISCLAIMER}</p>
      </div>

      <div style={{ display: "grid", gap: 24 }}>
        <h3 className="pq-title">Cuándo ir</h3>
        <div className="pq-row">
          {seasons.map(({ label, range, note }) => (
            <article key={label} className="pq-card">
              <p className="pq-meta">{label}</p>
              <p className="pq-season">{range}</p>
              <p className="pq-small">{note}</p>
            </article>
          ))}
        </div>
      </div>

      <div style={{ display: "grid", gap: 24 }}>
        <h3 className="pq-title">Antes de entrar</h3>
        <ul className="pq-checks">
          {tips.map((tip) => (
            <li key={tip}>
              <Check size={18} strokeWidth={2.25} aria-hidden="true" />
              <span>{tip}</span>
            </li>
          ))}
        </ul>
      </div>

      <ul className="pq-tags" aria-label="Enlaces oficiales">
        {links.map(({ label, href }) => (
          <li key={label}>
            <a href={href} target="_blank" rel="noopener noreferrer" className="pq-btn pq-btn-outline">
              {label}
              <ExternalLink size={14} aria-hidden="true" />
            </a>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

export default TourismInfo;
